import { useEffect, useState } from "react";

import { fetchRegistryRecord, type RegistryLookup } from "./record";

/**
 * Loading a registry record for a page.
 *
 * The state machine is deliberately four-valued rather than the usual
 * `data | error`, because on these pages the difference between the failures is
 * the whole product:
 *
 *  - `loading` must never render as an answer. A page that flashes "tidak
 *    terdaftar" before its fetch resolves has told the visitor the opposite of
 *    the truth for a few hundred milliseconds, and that is long enough to read.
 *  - `unconfigured` means the registry is not deployed here. It is not a 404 and
 *    must not look like one.
 *  - `unreachable` means the chain could not be read. Also not a 404.
 *  - only a resolved record with status `Unregistered` is the negative answer.
 */
export type LookupState = { status: "loading" } | { status: "ready"; lookup: RegistryLookup };

export function useRegistryLookup(registryId: string | undefined, version?: number): LookupState {
  /*
    The result is stored *with* the target it answers, and read back only when
    the two still match.

    The obvious alternative — resetting to `loading` at the top of the effect —
    sets state synchronously during an effect, which costs a second render pass
    on every navigation. More importantly this shape also fixes the race it was
    meant to paper over: an answer for the previous id can no longer be painted
    under the new one, because it simply does not match the key being read.
  */
  const [answered, setAnswered] = useState<{ key: string; lookup: RegistryLookup } | null>(null);

  const key = registryId === undefined ? "" : `${registryId}@${version ?? "latest"}`;

  useEffect(() => {
    if (registryId === undefined) return;

    const controller = new AbortController();
    void fetchRegistryRecord(registryId, { version, signal: controller.signal }).then((lookup) => {
      if (!controller.signal.aborted) setAnswered({ key, lookup });
    });

    return () => controller.abort();
  }, [key, registryId, version]);

  if (answered === null || answered.key !== key) return { status: "loading" };
  return { status: "ready", lookup: answered.lookup };
}
